import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../../../services/apiService';
import {
  Btn, Chip, Loading, Msg, Section, Select, TextInput, fmtDate, panelGet, useAdminGet,
} from '../kit';
import TechCardModal from '../search/TechCardModal';

/**
 * Universities → TRL provenance: where the TRL on every technology card comes
 * from, and how far it can be trusted.
 *
 * The card shows one number, but it can mean five different things: the TTO
 * said so and the evidence pass agrees; the TTO said so and the evidence
 * points elsewhere; the TTO said so and there was nothing to check it
 * against; the TTO said nothing and we inferred it; or nobody knows. The
 * backend (admin_trl_provenance.py) sorts every row of the corpus into
 * exactly one of those five from technologies.development_stage — one SQL
 * CASE shared by every read and the Excel export — so the cards, the table,
 * the drill-down and the workbook always agree and always sum to the corpus.
 *
 * This file only lays that out: one filter bar feeding three panel reads
 * (universities/trl_overview, trl_by_university, trl_technologies) and the
 * export route; the five categories as cards and one 100 % bar; the displayed
 * TRL inside each category; the disclosed-vs-evidence grid for the two
 * categories that have both numbers; one row per university; and a paged
 * drill-down that any count opens. Filter state is flat and all-optional
 * because it is exactly the query string the backend takes.
 */

// ---------------------------------------------------------------------------
// The five categories — the owner's wording, in the owner's order
// ---------------------------------------------------------------------------
export type CatKey = 'disclosed_confirmed' | 'disclosed_mismatch' | 'disclosed_no_evidence'
  | 'inferred' | 'not_disclosed_no_evidence';

interface Cat { key: CatKey; no: number; label: string; color: string; ink: string }

// Colours follow the brief (1 green, 2 amber, 3 blue-grey, 4 teal, 5 grey),
// stepped until every pair separates under protan/deutan/tritan simulation
// (dataviz validator, all pairs: worst CVD ΔE 9.6, normal-vision ΔE 16.7).
// Slots 3 and 5 are grey by brief, so the number 1-5 rides with the colour
// everywhere it appears — colour is never the only way to tell them apart.
export const CATEGORIES: Cat[] = [
  { key: 'disclosed_confirmed', no: 1, label: 'TRL value as disclosed by TTO',
    color: '#22c55e', ink: '#141414' },
  { key: 'disclosed_mismatch', no: 2, label: 'TRL evidence found but not matching with disclosed TRL value',
    color: '#b45309', ink: '#ffffff' },
  { key: 'disclosed_no_evidence', no: 3, label: 'TRL value as disclosed by TTO but no evidence found',
    color: '#6b7f99', ink: '#ffffff' },
  { key: 'inferred', no: 4, label: 'TRL value inferred from evidence found',
    color: '#0f5f58', ink: '#ffffff' },
  { key: 'not_disclosed_no_evidence', no: 5, label: 'TRL value not disclosed and TRL evidence not found',
    color: '#b3b3b3', ink: '#141414' },
];
const CAT_BY_KEY: Record<string, Cat> = Object.fromEntries(CATEGORIES.map(c => [c.key, c]));
const catOf = (v: any): Cat | undefined => (v ? CAT_BY_KEY[String(v)] : undefined);
const MATRIX_CATS: CatKey[] = ['disclosed_confirmed', 'disclosed_mismatch'];
const WEAK_LABEL = 'TRL evidence found but too weak to assign a level';

const EXPORT_PATH = '/api/v1/admin/universities/trl-export.xlsx';
const TRLS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
const CONF_STEPS = ['', '0', '0.5', '1', '1.5', '2', '2.5', '3', '3.5', '4', '4.5', '5'];
// The backend's three directions (category 2 only, both levels known).
const DIRECTIONS: Record<string, string> = {
  '': 'Any direction (category 2)',
  higher: 'Evidence TRL higher than disclosed',
  lower: 'Evidence TRL lower than disclosed',
  equal: 'Same first number (e.g. a range)',
};

// ---------------------------------------------------------------------------
// Filters — flat, all optional, and exactly the query string
// ---------------------------------------------------------------------------
type ListKey = 'countries' | 'universities' | 'categories' | 'displayed_trl' | 'evidence_trl' | 'industries';
const LIST_KEYS: ListKey[] = ['countries', 'universities', 'categories', 'displayed_trl', 'evidence_trl', 'industries'];
const LIST_LABELS: Record<ListKey, string> = {
  countries: 'Country', universities: 'University', categories: 'Category',
  displayed_trl: 'Displayed TRL', evidence_trl: 'Evidence TRL', industries: 'Industry',
};

interface Filters {
  countries: string[]; universities: string[]; categories: string[];
  displayed_trl: string[]; evidence_trl: string[]; industries: string[];
  min_confidence: string; max_confidence: string; direction: string; q: string;
}
const EMPTY: Filters = {
  countries: [], universities: [], categories: [], displayed_trl: [], evidence_trl: [], industries: [],
  min_confidence: '', max_confidence: '', direction: '', q: '',
};

/** What a drill-down adds on top of the page's filters. */
type Scope = Partial<Filters> & { weak_evidence?: boolean };

/** A matrix cell the drill-down is narrowed to (see useCellRows): the pair,
 *  which grid it was read from, its count and how many rows of its evidence
 *  column sort above it (disclosed TRL descending). */
type GridView = 'both' | CatKey;
interface Cell { d: number; e: number; view: GridView; n: number; offset: number }
const cellFrom = (grid: number[][], d: number, e: number, view: GridView): Cell => {
  let offset = 0;
  for (let dd = 9; dd > d; dd--) offset += grid[dd - 1][e - 1];
  return { d, e, view, n: grid[d - 1][e - 1], offset };
};

const toParams = (f: Scope): Record<string, string> => {
  const p: Record<string, string> = {};
  for (const k of LIST_KEYS) {
    const v = f[k];
    if (!v || !v.length) continue;
    // Industry names carry commas ("ICT, AI & Computing"); the backend takes
    // a JSON array as-is, so they travel that way and never get re-split.
    p[k] = k === 'industries' ? JSON.stringify(v) : v.join(',');
  }
  if (f.min_confidence) p.min_confidence = f.min_confidence;
  if (f.max_confidence) p.max_confidence = f.max_confidence;
  if (f.direction) p.direction = f.direction;
  if (f.q && f.q.trim()) p.q = f.q.trim();
  if (f.weak_evidence) p.weak_evidence = '1';
  return p;
};

/** Page filters narrowed by a drill scope. A list the scope sets is
 *  intersected with the page's, never widened: the count that was clicked
 *  was computed inside the page's filters. Nothing in common = null, an
 *  honest empty list (the backend does the same for its own `category`). */
const narrow = (base: Filters, scope: Scope): Scope | null => {
  const out: Scope = { ...base };
  for (const k of LIST_KEYS) {
    const s = scope[k];
    if (!s || !s.length) continue;
    const b = base[k];
    const both = b.length ? s.filter(v => b.includes(v)) : s;
    if (!both.length) return null;
    out[k] = both;
  }
  if (scope.direction) {
    if (base.direction && base.direction !== scope.direction) return null;
    out.direction = scope.direction;
  }
  if (scope.weak_evidence) out.weak_evidence = true;
  return out;
};

// ---------------------------------------------------------------------------
// Reading the server's answers
// ---------------------------------------------------------------------------
const num = (v: any): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const pctOf = (n: number, total: number) => (total > 0 ? (n / total) * 100 : 0);
const fmtN = (n: number) => n.toLocaleString();
const fmtPct = (p: number) => (p > 0 && p < 0.05 ? '<0.1%' : `${p.toFixed(1)}%`);

type CatCounts = Record<CatKey, number>;
const zeroCounts = (): CatCounts => Object.fromEntries(CATEGORIES.map(c => [c.key, 0])) as CatCounts;
const zeroGrid = (): number[][] => Array.from({ length: 9 }, () => new Array(9).fill(0));
const asGrid = (g: any): number[][] => {
  const out = zeroGrid();
  if (Array.isArray(g)) g.slice(0, 9).forEach((row: any, i: number) => {
    if (Array.isArray(row)) row.slice(0, 9).forEach((n: any, j: number) => { out[i][j] = num(n); });
  });
  return out;
};

interface Facets {
  countries: { value: string; n: number }[];
  universities: { code: string; name: string; country: string; n: number }[];
  industries: { value: string; n: number }[];
}

interface Overview {
  total: number; counts: CatCounts; weak: number;
  byTrl: Record<CatKey, number[]>;
  grid: number[][]; gridByCat: Record<string, number[][]>; unmapped: number;
  direction: { higher: number; lower: number; equal: number; unknown: number };
  generatedAt: string; facets: Facets | null;
}

// Shape: admin_trl_provenance.overview().
const readOverview = (d: any): Overview | null => {
  if (!d || typeof d !== 'object') return null;
  const counts = zeroCounts();
  const byTrl = Object.fromEntries(CATEGORIES.map(c => [c.key, new Array(10).fill(0)])) as Record<CatKey, number[]>;
  for (const c of Array.isArray(d.categories) ? d.categories : []) {
    const cat = catOf(c?.key);
    if (!cat) continue;
    counts[cat.key] = num(c.n);
    for (const it of Array.isArray(c.by_displayed_trl) ? c.by_displayed_trl : []) {
      const t = num(it?.trl);
      if (t >= 0 && t <= 9) byTrl[cat.key][t] += num(it?.n);
    }
  }
  const dve = d.disclosed_vs_evidence || {};
  const gridByCat: Record<string, number[][]> = {};
  for (const k of MATRIX_CATS) gridByCat[k] = asGrid(dve.grid_by_category?.[k]);
  const dir = d.direction || {};
  const f = d.facets;
  return {
    total: num(d.total), counts, weak: num(d.weak_evidence), byTrl,
    grid: asGrid(dve.grid), gridByCat,
    unmapped: Object.values(dve.unmapped || {}).reduce<number>((a, v) => a + num(v), 0),
    direction: { higher: num(dir.higher), lower: num(dir.lower), equal: num(dir.equal), unknown: num(dir.unknown) },
    generatedAt: String(d.generated_at || ''),
    facets: f && typeof f === 'object' ? {
      countries: Array.isArray(f.countries) ? f.countries.map((x: any) => ({ value: String(x.value), n: num(x.n) })) : [],
      universities: Array.isArray(f.universities)
        ? f.universities.map((x: any) => ({ code: String(x.code || ''), name: String(x.name || x.code || ''),
            country: String(x.country || ''), n: num(x.n) })).filter((x: any) => x.code) : [],
      industries: Array.isArray(f.industries) ? f.industries.map((x: any) => ({ value: String(x.value), n: num(x.n) })) : [],
    } : null,
  };
};

interface UniRow {
  code: string; name: string; country: string; total: number; counts: CatCounts; weak: number;
  disclosed: number; evidence: number;       // shares, 0-100, recomputed here from the counts
}
// Shape: admin_trl_provenance.by_university().rows.
const readUniRows = (d: any): UniRow[] => (Array.isArray(d?.rows) ? d.rows : []).map((r: any) => {
  const counts = zeroCounts();
  for (const c of CATEGORIES) counts[c.key] = num(r.counts?.[c.key] ?? r[c.key]);
  const total = num(r.total);
  return {
    code: String(r.code ?? ''), name: String(r.name || r.code || ''), country: String(r.country || ''),
    total, counts, weak: num(r.weak_evidence),
    disclosed: pctOf(counts.disclosed_confirmed + counts.disclosed_mismatch + counts.disclosed_no_evidence, total),
    evidence: pctOf(counts.disclosed_confirmed + counts.inferred, total),
  };
});

// ---------------------------------------------------------------------------
// Small pieces
// ---------------------------------------------------------------------------
const saveBlob = (blob: Blob, name: string) => {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
};

/** The category's number on its colour — the second cue the two grey slots
 *  need, used everywhere a category colour appears. */
const CatDot: React.FC<{ cat: Cat }> = ({ cat }) => (
  <span aria-hidden
    className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold shrink-0 leading-none"
    style={{ background: cat.color, color: cat.ink }}>
    {cat.no}
  </span>
);

/** Checkbox dropdown — checkboxes rather than chips, easier to scan and pick. */
const MultiPick: React.FC<{
  label: string; options: { value: string; label: string; n?: number }[];
  selected: string[]; onChange: (v: string[]) => void; searchable?: boolean; wide?: boolean;
}> = ({ label, options, selected, onChange, searchable, wide }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const off = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', off);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', off); document.removeEventListener('keydown', esc); };
  }, [open]);
  const view = useMemo(() => {
    const n = q.trim().toLowerCase();
    return n ? options.filter(o => o.label.toLowerCase().includes(n) || o.value.toLowerCase().includes(n)) : options;
  }, [options, q]);
  const toggle = (v: string) =>
    onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);
  const nameOf = (v: string) => options.find(o => o.value === v)?.label || v;
  return (
    <div className="relative max-w-full" ref={box}>
      <button type="button" onClick={() => setOpen(o => !o)}
        className={`h-9 px-3 rounded-md border-[0.5px] bg-white text-sm whitespace-nowrap font-grotesk max-w-full sm:max-w-[18rem] truncate
          ${selected.length ? 'border-primary-500 text-grayscale-1300' : 'border-primary-300 text-grayscale-1200'}`}
        title={selected.length ? selected.map(nameOf).join('\n') : `Any ${label.toLowerCase()}`}>
        {label}: <b className="text-grayscale-1300">
          {selected.length === 0 ? 'All' : selected.length === 1 ? nameOf(selected[0]) : `${selected.length} selected`}
        </b> ▾
      </button>
      {open && (
        <div className={`absolute z-40 mt-1 ${wide ? 'w-96' : 'w-72'} max-w-[90vw] max-h-96 overflow-auto bg-white rounded-xl
          border border-grayscale-300 p-3 flex flex-col gap-2 shadow-lg`}>
          <div className="flex items-center gap-2">
            <Btn small onClick={() => onChange(Array.from(new Set([...selected, ...view.map(o => o.value)])))}>
              All{q ? ' shown' : ''}
            </Btn>
            <Btn small onClick={() => onChange([])}>None</Btn>
            <span className="text-xs text-grayscale-1100 flex-1">{selected.length} selected</span>
            <Btn small kind="primary" onClick={() => setOpen(false)}>Done</Btn>
          </div>
          {searchable && <TextInput value={q} onChange={setQ} placeholder="Search…" wide />}
          <div className="flex flex-col gap-0.5">
            {view.map(o => (
              <label key={o.value}
                className="flex items-start gap-2 text-sm text-grayscale-1300 px-1 py-0.5 rounded hover:bg-grayscale-200 cursor-pointer">
                <input type="checkbox" className="mt-1" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} />
                <span className="leading-snug flex-1">{o.label}</span>
                {o.n !== undefined && <span className="text-[11px] text-grayscale-1100 tabular-nums mt-0.5">{fmtN(o.n)}</span>}
              </label>
            ))}
            {!view.length && <span className="text-xs text-grayscale-1100 px-1">{options.length ? 'No matches.' : 'Loading options…'}</span>}
          </div>
        </div>
      )}
    </div>
  );
};

/** One horizontal 100 % bar. Segments sit 2 px apart; a label goes inside a
 *  segment only when there is room for it; every segment has a tooltip. */
interface Seg {
  key: string; n: number; color: string; ink: string; title: string; onClick?: () => void;
  /** Label inside the segment when it is wide enough; `short` when it is not. */
  inside?: string; short?: string;
}
const StackBar: React.FC<{ segs: Seg[]; height?: string; minLabelPct?: number }> =
  ({ segs, height = 'h-7', minLabelPct = 7 }) => {
  const total = segs.reduce((a, s) => a + s.n, 0);
  if (!total) return <div className={`${height} rounded-md bg-grayscale-200`} />;
  return (
    <div className={`flex gap-[2px] ${height} w-full rounded-md overflow-hidden`}>
      {segs.filter(s => s.n > 0).map(s => {
        const p = (s.n / total) * 100;
        const text = s.inside && p >= minLabelPct ? s.inside : s.short && p >= 1.5 ? s.short : '';
        const label = text ? <span className="px-1 truncate">{text}</span> : null;
        const cls = 'h-full min-w-[3px] flex items-center justify-center overflow-hidden text-[10px] font-bold';
        const style = { flexGrow: s.n, flexBasis: 0, background: s.color, color: s.ink };
        return s.onClick ? (
          <button key={s.key} type="button" onClick={s.onClick} title={s.title}
            className={`${cls} cursor-pointer hover:opacity-80`} style={style}>{label}</button>
        ) : (
          <div key={s.key} title={s.title} className={cls} style={style}>{label}</div>
        );
      })}
    </div>
  );
};

/** Displayed TRL 0-9 as one hue, light → dark; 0 (no level) is neutral grey
 *  so it never reads as "TRL 1, only lighter". */
const trlColor = (t: number): string => {
  if (t <= 0) return '#e5e5e5';
  const lo = [234, 223, 204]; const hi = [75, 58, 34];
  const k = (t - 1) / 8;
  return `rgb(${lo.map((a, i) => Math.round(a + (hi[i] - a) * k)).join(',')})`;
};
const trlInk = (t: number) => (t >= 6 ? '#ffffff' : '#141414');

type SortState = { key: string; dir: 'asc' | 'desc' };
const Th: React.FC<{
  label: React.ReactNode; k?: string; sort?: SortState; onSort?: (k: string) => void;
  className?: string; title?: string;
}> = ({ label, k, sort, onSort, className = '', title }) => {
  const on = !!k && sort?.key === k;
  const sortable = !!k && !!onSort;
  return (
    <th onClick={sortable ? () => onSort!(k!) : undefined}
      title={title || (sortable ? (on ? `Sorted ${sort?.dir === 'asc' ? 'ascending' : 'descending'} — click to flip` : 'Click to sort') : undefined)}
      className={`text-left font-grotesk text-xs px-3 py-2 align-bottom bg-grayscale-200 select-none
        ${sortable ? 'cursor-pointer hover:text-grayscale-1300' : ''} ${on ? 'text-grayscale-1300 font-bold' : 'text-grayscale-1200'} ${className}`}>
      {label}
      {on && <span aria-hidden className="ml-1 text-[10px] opacity-70">{sort?.dir === 'asc' ? '▲' : '▼'}</span>}
    </th>
  );
};

// ---------------------------------------------------------------------------
// The tab
// ---------------------------------------------------------------------------
interface Drill { scope: Scope; title: string; nonce: number; cell?: Cell }

const TrlProvenance: React.FC = () => {
  const [f, setF] = useState<Filters>(EMPTY);
  const [qDraft, setQDraft] = useState('');
  const [drill, setDrill] = useState<Drill | null>(null);
  const [dlBusy, setDlBusy] = useState(false);
  const [dlErr, setDlErr] = useState('');
  const [facets, setFacets] = useState<Facets | null>(null);
  const drillRef = useRef<HTMLDivElement>(null);

  // The title search waits for a pause in typing rather than re-reading the
  // corpus on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setF(s => (s.q === qDraft ? s : { ...s, q: qDraft })), 400);
    return () => clearTimeout(t);
  }, [qDraft]);

  const params = useMemo(() => toParams(f), [f]);
  const key = JSON.stringify(params);
  const ov = useAdminGet('universities', 'trl_overview', params, [key]);
  const bu = useAdminGet('universities', 'trl_by_university', params, [key]);
  const o = useMemo(() => readOverview(ov.data), [ov.data]);
  const uniRows = useMemo(() => readUniRows(bu.data), [bu.data]);

  // Option lists are the overview's facets — computed over the whole corpus,
  // so ticking one country does not make the others vanish from its list.
  useEffect(() => { if (o?.facets) setFacets(o.facets); }, [o]);
  // An open matrix-cell drill-down follows the page filters: when the
  // overview comes back for new filters, re-read the cell's count and row
  // window from the new grid (rows are also checked pair by pair, so the
  // moment in between can show fewer rows, never wrong ones).
  useEffect(() => {
    if (!o) return;
    setDrill(d => {
      if (!d?.cell) return d;
      const g = d.cell.view === 'both' ? o.grid : (o.gridByCat[d.cell.view] || zeroGrid());
      const next = cellFrom(g, d.cell.d, d.cell.e, d.cell.view);
      return next.n === d.cell.n && next.offset === d.cell.offset ? d : { ...d, cell: next };
    });
  }, [o]);

  const countryOpts = useMemo(() => (facets?.countries || [])
    .map(c => ({ value: c.value, label: c.value, n: c.n })), [facets]);
  const uniOpts = useMemo(() => (facets?.universities || [])
    .filter(u => !f.countries.length || f.countries.includes(u.country))
    .map(u => ({ value: u.code, label: `${u.name}${u.country ? ` · ${u.country}` : ''}`, n: u.n })), [facets, f.countries]);
  const uniName = useMemo(() => {
    const m: Record<string, string> = {};
    for (const u of facets?.universities || []) m[u.code] = u.name;
    for (const r of uniRows) m[r.code] = m[r.code] || r.name;
    return m;
  }, [facets, uniRows]);
  const indOpts = useMemo(() => (facets?.industries || [])
    .map(i => ({ value: i.value, label: i.value, n: i.n })), [facets]);
  const catOpts = CATEGORIES.map(c => ({ value: c.key, label: `${c.no}. ${c.label}` }));
  const dispOpts = TRLS.map(t => ({ value: t, label: t === '0' ? '0 — no level on the card' : `TRL ${t}` }));
  const evOpts = TRLS.slice(1).map(t => ({ value: t, label: `TRL ${t}` }));

  const setList = (k: ListKey) => (v: string[]) => setF(s => ({ ...s, [k]: v }));
  const valueLabel = (k: ListKey, v: string): string => {
    if (k === 'categories') { const c = catOf(v); return c ? `${c.no}. ${c.label}` : v; }
    if (k === 'universities') return uniName[v] || v;
    if (k === 'displayed_trl') return v === '0' ? 'none' : `TRL ${v}`;
    if (k === 'evidence_trl') return `TRL ${v}`;
    return v;
  };
  const chips: { label: string; value: string; clear: () => void }[] = [];
  for (const k of LIST_KEYS) {
    if (f[k].length) chips.push({ label: LIST_LABELS[k], value: f[k].map(v => valueLabel(k, v)).join(', '), clear: () => setList(k)([]) });
  }
  if (f.min_confidence || f.max_confidence) {
    chips.push({ label: 'Confidence', value: `${f.min_confidence || '0'} – ${f.max_confidence || '5'}`,
      clear: () => setF(s => ({ ...s, min_confidence: '', max_confidence: '' })) });
  }
  if (f.direction) chips.push({ label: 'Direction', value: DIRECTIONS[f.direction], clear: () => setF(s => ({ ...s, direction: '' })) });
  if (f.q) chips.push({ label: 'Title', value: f.q, clear: () => { setQDraft(''); setF(s => ({ ...s, q: '' })); } });
  const resetAll = () => { setQDraft(''); setF(EMPTY); };
  const confBad = !!(f.min_confidence && f.max_confidence && Number(f.min_confidence) > Number(f.max_confidence));

  const open = (scope: Scope, title: string, cell?: Cell) => setDrill({ scope, title, cell, nonce: Date.now() });
  useEffect(() => {
    if (drill) drillRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [drill?.nonce]);   // eslint-disable-line react-hooks/exhaustive-deps

  const download = async (p: Record<string, string>) => {
    setDlBusy(true); setDlErr('');
    const { blob, filename, error } = await apiService.adminFetchFile(
      apiService.getStoredUserId() || '', EXPORT_PATH, p);
    setDlBusy(false);
    if (error || !blob) { setDlErr(error || 'Download failed'); return; }
    const ymd = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    saveBlob(blob, filename || `trl-provenance-${ymd}.xlsx`);
  };

  const toggleUniversity = (code: string) =>
    setF(s => ({ ...s, universities: s.universities.length === 1 && s.universities[0] === code ? [] : [code] }));

  const busy = ov.loading || bu.loading;
  const total = o?.total || 0;

  return (
    <div className="flex flex-col gap-4">
      {/* ---------------- Filters ---------------- */}
      <Section title="Filters"
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] text-grayscale-1100">
              {busy ? 'Updating…' : o?.generatedAt ? `computed ${fmtDate(o.generatedAt)}` : ''}
            </span>
            <Btn small onClick={() => { ov.reload(); bu.reload(); }} disabled={busy}>Refresh</Btn>
            <Btn small kind="primary" onClick={() => download(params)} disabled={dlBusy || confBad}
              title="Overview, By university and Technologies sheets for the filters in force (Technologies capped at 30,000 rows)">
              {dlBusy ? 'Preparing…' : 'Download Excel'}
            </Btn>
          </div>
        }>
        <div className="flex items-center gap-2 flex-wrap">
          <MultiPick label="Country" options={countryOpts} selected={f.countries} onChange={setList('countries')} />
          <MultiPick label="University" options={uniOpts} selected={f.universities} onChange={setList('universities')} searchable wide />
          <MultiPick label="Category" options={catOpts} selected={f.categories} onChange={setList('categories')} wide />
          <MultiPick label="Displayed TRL" options={dispOpts} selected={f.displayed_trl} onChange={setList('displayed_trl')} />
          <MultiPick label="Evidence TRL" options={evOpts} selected={f.evidence_trl} onChange={setList('evidence_trl')} />
          <MultiPick label="Industry" options={indOpts} selected={f.industries} onChange={setList('industries')} searchable wide />
        </div>
        <div className="flex items-center gap-2 flex-wrap [&_select]:max-w-full">
          <span className="text-xs text-grayscale-1200 whitespace-nowrap"
            title="development_stage.confidence_score, 0-5 (a technology with none counts as 0)">Confidence</span>
          <Select value={f.min_confidence} onChange={v => setF(s => ({ ...s, min_confidence: v }))}
            options={CONF_STEPS} labels={{ '': 'min' }} />
          <span className="text-xs text-grayscale-1100">to</span>
          <Select value={f.max_confidence} onChange={v => setF(s => ({ ...s, max_confidence: v }))}
            options={CONF_STEPS} labels={{ '': 'max' }} />
          <Select value={f.direction} onChange={v => setF(s => ({ ...s, direction: v }))}
            options={Object.keys(DIRECTIONS)} labels={DIRECTIONS} />
          <TextInput value={qDraft} onChange={setQDraft} placeholder="Title contains…" wide />
          {chips.length > 0 && (
            <button type="button" onClick={resetAll}
              className="text-xs text-grayscale-1100 underline underline-offset-2">reset all</button>
          )}
        </div>
        {f.direction && (
          <p className="text-[11px] text-grayscale-1100 -mt-1">
            Direction only exists for category 2 (evidence found but not matching) where both levels are
            known, so this filter keeps just those technologies.
          </p>
        )}
        {chips.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap">
            {chips.map(c => <Chip key={c.label} label={c.label} value={c.value} onClear={c.clear} />)}
          </div>
        )}
      </Section>

      {confBad && <Msg kind="error">The minimum confidence is above the maximum.</Msg>}
      {dlErr && <Msg kind="error">Excel export failed: {dlErr}</Msg>}
      {ov.error && !confBad && <Msg kind="error">{ov.error}</Msg>}

      {ov.loading && !o ? <Loading label="Categorising the corpus…" /> : o && (
        <div className={`flex flex-col gap-4 transition-opacity ${busy ? 'opacity-60' : ''}`}>
          {/* ---------------- Headline ---------------- */}
          <Section title="Where the TRL on each card comes from">
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
              <button type="button" onClick={() => open({}, 'All technologies matching the filters')}
                title="List every technology matching the filters"
                className="rounded-xl border border-grayscale-300 bg-white px-4 py-3 text-left hover:border-primary-400 hover:bg-primary-50">
                <div className="text-[11px] uppercase tracking-wide text-grayscale-1100">Technologies</div>
                <div className="text-xl font-bold text-grayscale-1300 font-grotesk">{fmtN(total)}</div>
                <div className="text-[11px] text-grayscale-1100">{chips.length ? 'matching the filters' : 'the whole corpus'}</div>
              </button>
              {CATEGORIES.map(c => {
                const n = o.counts[c.key];
                const active = !drill?.cell && !drill?.scope.weak_evidence && Object.keys(drill?.scope || {}).length === 1
                  && drill?.scope.categories?.length === 1 && drill.scope.categories[0] === c.key;
                return (
                  <div key={c.key}
                    className={`rounded-xl border bg-white text-left flex flex-col
                      ${active ? 'border-primary-500 ring-1 ring-primary-500' : 'border-grayscale-300'}`}
                    style={{ borderTop: `4px solid ${c.color}` }}>
                    <button type="button" disabled={!n}
                      onClick={() => open({ categories: [c.key] }, c.label)}
                      title={n ? `List these ${fmtN(n)} technologies` : 'None in these filters'}
                      className={`px-4 pt-3 pb-1 text-left flex flex-col gap-1 flex-1 rounded-b-xl
                        ${n ? 'hover:bg-primary-50' : 'opacity-60 cursor-default'}`}>
                      <span className="flex items-start gap-1.5">
                        <CatDot cat={c} />
                        <span className="text-[11px] leading-snug text-grayscale-1200">{c.label}</span>
                      </span>
                      <span className="text-xl font-bold text-grayscale-1300 font-grotesk mt-auto">{fmtN(n)}</span>
                      <span className="text-[11px] text-grayscale-1100">{fmtPct(pctOf(n, total))} of {fmtN(total)}</span>
                    </button>
                    {c.key === 'not_disclosed_no_evidence' && o.weak > 0 && (
                      <button type="button"
                        onClick={() => open({ weak_evidence: true }, `${c.label} · of which: ${WEAK_LABEL.toLowerCase()}`)}
                        title="Still counted in category 5 — pass 1 found some evidence, too weak to assign a level. Click to list them."
                        className="px-4 pb-2 text-left text-[11px] text-primary-700 underline underline-offset-2">
                        of which {fmtN(o.weak)} with weak evidence
                      </button>
                    )}
                    {!(c.key === 'not_disclosed_no_evidence' && o.weak > 0) && <span className="pb-2" />}
                  </div>
                );
              })}
            </div>
            <StackBar height="h-8"
              segs={CATEGORIES.map(c => ({
                key: c.key, n: o.counts[c.key], color: c.color, ink: c.ink,
                inside: `${c.no} · ${fmtPct(pctOf(o.counts[c.key], total))}`, short: String(c.no),
                title: `${c.no}. ${c.label}\n${fmtN(o.counts[c.key])} · ${fmtPct(pctOf(o.counts[c.key], total))}`,
                onClick: () => open({ categories: [c.key] }, c.label),
              }))} />
            <details className="text-xs text-grayscale-1100">
              <summary className="cursor-pointer select-none">How a technology is placed in a category</summary>
              <ul className="list-disc pl-5 mt-1 flex flex-col gap-0.5">
                <li><b>1</b> — the TTO disclosed a TRL (1-9), pass 1 found TRL evidence, and pass 2 says the evidence supports the disclosed value.</li>
                <li><b>2</b> — the TTO disclosed a TRL and pass 1 found evidence, but the evidence does not support the disclosed value.</li>
                <li><b>3</b> — the TTO disclosed a TRL, but pass 1 found no evidence (or the two-pass check never ran).</li>
                <li><b>4</b> — the TTO disclosed nothing and a level (1-9) was inferred from the evidence.</li>
                <li><b>5</b> — everything else, including technologies where pass 1 found some evidence but too weak to assign a level (&quot;weak evidence&quot;, a subset of 5, not a sixth category).</li>
              </ul>
              <p className="mt-1">
                <b>Displayed TRL</b> is what the card shows (calculated TRL, else interpreted TRL; 0 = no level).
                <b> Evidence TRL</b> is the level the evidence pass settled on. Every technology is in exactly one
                category, so the five add up to the total.
              </p>
            </details>
          </Section>

          {/* ---------------- TRL inside each category + matrix ---------------- */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <Section title="Displayed TRL within each category">
              <div className="flex items-center gap-1 flex-wrap text-[10px] text-grayscale-1100">
                <span className="mr-1">Displayed TRL:</span>
                {TRLS.map(t => (
                  <span key={t} className="inline-flex items-center gap-1 mr-1.5">
                    <span className="inline-block w-3 h-3 rounded-sm" style={{ background: trlColor(Number(t)) }} />
                    {t === '0' ? 'none' : t}
                  </span>
                ))}
              </div>
              <div className="flex flex-col gap-3">
                {CATEGORIES.map(c => {
                  const arr = o.byTrl[c.key];
                  const n = arr.reduce((a, b) => a + b, 0);
                  return (
                    <div key={c.key} className="flex flex-col gap-1">
                      <div className="flex items-start gap-1.5 text-xs">
                        <CatDot cat={c} />
                        <span className="text-grayscale-1200 flex-1 leading-snug">{c.label}</span>
                        <span className="text-grayscale-1300 tabular-nums font-medium">{fmtN(o.counts[c.key])}</span>
                      </div>
                      {n ? (
                        <StackBar height="h-5" minLabelPct={6}
                          segs={arr.map((cnt, t) => ({
                            key: String(t), n: cnt, color: trlColor(t), ink: trlInk(t),
                            inside: t === 0 ? '–' : String(t),
                            title: `${c.no}. ${c.label}\nDisplayed TRL ${t === 0 ? 'none' : t}: ${fmtN(cnt)} (${fmtPct(pctOf(cnt, n))} of this category)`,
                            onClick: () => open({ categories: [c.key], displayed_trl: [String(t)] },
                              `${c.label} · displayed TRL ${t === 0 ? 'none' : t}`),
                          }))} />
                      ) : (
                        <div className="text-[11px] text-grayscale-1100">None in these filters.</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Section>

            <MatrixSection o={o} onOpen={open} />
          </div>

          {/* ---------------- Per university ---------------- */}
          <UniversityTable rows={uniRows} overview={o} loading={bu.loading && !bu.data} error={bu.error}
            selected={f.universities} onToggleUniversity={toggleUniversity} onOpen={open} />
        </div>
      )}

      {/* ---------------- Drill-down ---------------- */}
      {drill && (
        <div ref={drillRef} className="scroll-mt-4">
          <DrillDown key={drill.nonce} base={f} drill={drill} describe={valueLabel}
            // Changing the scope by hand leaves the exact-cell mode: its row
            // window was worked out for the scope the cell was clicked in.
            onScope={scope => setDrill(d => (d ? { ...d, scope, cell: undefined } : d))}
            onDropCell={() => setDrill(d => (d ? { ...d, cell: undefined } : d))}
            onClose={() => setDrill(null)}
            onDownload={download} dlBusy={dlBusy} />
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Disclosed × evidence heat grid (categories 1 and 2)
// ---------------------------------------------------------------------------
const MatrixSection: React.FC<{ o: Overview; onOpen: (s: Scope, title: string, cell?: Cell) => void }> =
  ({ o, onOpen }) => {
  const [which, setWhich] = useState<GridView>('both');
  const grid = which === 'both' ? o.grid : (o.gridByCat[which] || zeroGrid());
  const cats: CatKey[] = which === 'both' ? MATRIX_CATS : [which];
  const max = Math.max(1, ...grid.flat());
  const rowSum = grid.map(r => r.reduce((a, b) => a + b, 0));
  const colSum = grid[0].map((_, ci) => grid.reduce((a, r) => a + r[ci], 0));
  const all = rowSum.reduce((a, b) => a + b, 0);
  const diag = grid.reduce((a, r, i) => a + r[i], 0);
  const above = grid.reduce((a, r, i) => a + r.slice(i + 1).reduce((x, y) => x + y, 0), 0);
  const alpha = (n: number) => 0.08 + 0.84 * Math.sqrt(n / max);    // sqrt so small cells still show
  const inCats = o.counts.disclosed_confirmed + o.counts.disclosed_mismatch;

  // The backend filters by evidence TRL but not by disclosed TRL, so the
  // drill-down asks for this evidence column sorted by disclosed TRL
  // (descending) and takes exactly the rows of this cell: the ones above it
  // in the column come first, and the grid already says how many.
  const cellClick = (d: number, e: number) => onOpen({ categories: cats, evidence_trl: [String(e)] },
    `Disclosed TRL ${d} → evidence TRL ${e}`, cellFrom(grid, d, e, which));
  const dirs: { k: 'higher' | 'lower' | 'equal' | 'unknown'; label: string; click: boolean }[] = [
    { k: 'higher', label: 'Evidence higher than disclosed', click: true },
    { k: 'lower', label: 'Evidence lower than disclosed', click: true },
    { k: 'equal', label: 'Same first number (e.g. a range)', click: true },
    { k: 'unknown', label: 'No level on one side', click: false },
  ];

  return (
    <Section title="Disclosed TRL vs evidence TRL"
      right={
        <Select value={which} onChange={v => setWhich(v as any)} options={['both', ...MATRIX_CATS]}
          labels={{ both: 'Categories 1 and 2', disclosed_confirmed: '1 only', disclosed_mismatch: '2 only' }} />
      }>
      <p className="text-xs text-grayscale-1100 -mt-1">
        Rows: the TRL the TTO disclosed. Columns: the TRL the evidence supports. The outlined diagonal is
        the same level; right of it the evidence puts the technology further along than disclosed, left of
        it less far. Click a cell to list its technologies.
      </p>
      {!inCats ? (
        <div className="text-sm text-grayscale-1100 py-4 text-center">No category 1 or 2 technologies in these filters.</div>
      ) : (
        <div className="flex flex-col gap-2">
          <div className="overflow-auto">
            <table className="text-xs border-separate" style={{ borderSpacing: 2 }}>
              <thead>
                <tr>
                  <th className="text-[10px] text-grayscale-1100 font-normal text-right pr-1 align-bottom whitespace-nowrap">
                    disclosed ↓ · evidence →
                  </th>
                  {grid[0].map((_, ci) => (
                    <th key={ci} className="w-11 text-center font-grotesk text-grayscale-1200 font-bold">{ci + 1}</th>
                  ))}
                  <th className="w-14 text-right text-grayscale-1100 font-normal pl-1">Σ</th>
                </tr>
              </thead>
              <tbody>
                {grid.map((row, ri) => (
                  <tr key={ri}>
                    <th className="text-right pr-1 font-grotesk text-grayscale-1200 font-bold whitespace-nowrap">TRL {ri + 1}</th>
                    {row.map((n, ci) => (
                      <td key={ci} className="p-0">
                        <button type="button" disabled={!n} onClick={() => cellClick(ri + 1, ci + 1)}
                          title={`Disclosed TRL ${ri + 1}, evidence TRL ${ci + 1}: ${fmtN(n)}${all ? ` (${fmtPct(pctOf(n, all))} of the grid)` : ''}`}
                          className={`w-11 h-8 rounded tabular-nums text-[11px]
                            ${n ? 'hover:ring-2 hover:ring-primary-500' : 'text-grayscale-600 cursor-default'}
                            ${ri === ci ? 'outline outline-1 outline-offset-[-1px] outline-grayscale-1100' : ''}`}
                          style={{ background: n ? `rgba(128, 101, 63, ${alpha(n).toFixed(3)})` : '#f5f5f5',
                                   color: n ? (alpha(n) > 0.5 ? '#ffffff' : '#141414') : undefined }}>
                          {n ? fmtN(n) : '·'}
                        </button>
                      </td>
                    ))}
                    <td className="text-right pl-1 tabular-nums text-grayscale-1200">{fmtN(rowSum[ri])}</td>
                  </tr>
                ))}
                <tr>
                  <th className="text-right pr-1 text-grayscale-1100 font-normal">Σ</th>
                  {colSum.map((n, ci) => (
                    <td key={ci} className="text-center tabular-nums text-grayscale-1200">{fmtN(n)}</td>
                  ))}
                  <td className="text-right pl-1 tabular-nums font-bold text-grayscale-1300">{fmtN(all)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="text-[11px] text-grayscale-1100">
            {fmtN(diag)} of {fmtN(all)} ({fmtPct(pctOf(diag, all))}) on the diagonal · {fmtN(above)} with the evidence
            higher · {fmtN(all - diag - above)} lower
            {which === 'both' && o.unmapped > 0 && <> · {fmtN(o.unmapped)} off the grid (no level on one side, e.g. a range left as &quot;Not Disclosed&quot;)</>}
          </div>
        </div>
      )}
      {o.counts.disclosed_mismatch > 0 && (
        <div className="flex flex-col gap-1.5 border-t border-grayscale-200 pt-3">
          <div className="text-xs text-grayscale-1200 flex items-center gap-1.5">
            <CatDot cat={CAT_BY_KEY.disclosed_mismatch} />
            Category 2 — which way the evidence disagrees ({fmtN(o.counts.disclosed_mismatch)})
          </div>
          <div className="flex gap-2 flex-wrap">
            {dirs.map(({ k, label, click }) => {
              const n = o.direction[k];
              const can = click && n > 0;
              return (
                <button key={k} type="button" disabled={!can}
                  onClick={() => onOpen({ categories: ['disclosed_mismatch'], direction: k },
                    `${CAT_BY_KEY.disclosed_mismatch.label} · ${label.toLowerCase()}`)}
                  title={can ? 'List these technologies' : undefined}
                  className={`rounded-lg border border-grayscale-300 px-3 py-1.5 text-left
                    ${can ? 'hover:border-primary-400 hover:bg-primary-50' : 'cursor-default'}`}>
                  <div className="text-[11px] text-grayscale-1100">{label}</div>
                  <div className="text-sm font-bold text-grayscale-1300 tabular-nums">
                    {fmtN(n)} <span className="text-[11px] font-normal text-grayscale-1100">
                      {fmtPct(pctOf(n, o.counts.disclosed_mismatch))}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </Section>
  );
};

// ---------------------------------------------------------------------------
// One row per university
// ---------------------------------------------------------------------------
const UniversityTable: React.FC<{
  rows: UniRow[]; overview: Overview; loading: boolean; error: string;
  selected: string[]; onToggleUniversity: (code: string) => void;
  onOpen: (scope: Scope, title: string) => void;
}> = ({ rows, overview, loading, error, selected, onToggleUniversity, onOpen }) => {
  const [sort, setSort] = useState<SortState>({ key: 'total', dir: 'desc' });
  const [measure, setMeasure] = useState<'share' | 'count'>('share');
  const clickSort = (k: string) => setSort(s => (s.key === k
    ? { key: k, dir: s.dir === 'asc' ? 'desc' : 'asc' }
    : { key: k, dir: k === 'name' || k === 'country' ? 'asc' : 'desc' }));

  const view = useMemo(() => {
    const val = (r: UniRow): number | string => {
      switch (sort.key) {
        case 'name': return r.name.toLowerCase();
        case 'country': return r.country.toLowerCase();
        case 'total': return r.total;
        case 'weak': return r.weak;
        case 'disclosed': return r.disclosed;
        case 'evidence': return r.evidence;
        default: {
          const n = r.counts[sort.key as CatKey] || 0;
          return measure === 'share' ? pctOf(n, r.total) : n;
        }
      }
    };
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = val(a); const vb = val(b);
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir || a.name.localeCompare(b.name);
      return String(va).localeCompare(String(vb)) * dir || a.name.localeCompare(b.name);
    });
  }, [rows, sort, measure]);

  const t = overview;
  const allDisclosed = pctOf(t.counts.disclosed_confirmed + t.counts.disclosed_mismatch + t.counts.disclosed_no_evidence, t.total);
  const allEvidence = pctOf(t.counts.disclosed_confirmed + t.counts.inferred, t.total);

  // `of` = 0 hides the share line (the Technologies column is 100 % of itself).
  const countCell = (n: number, of: number, onClick: () => void, strong?: boolean) => (n ? (
    <button type="button" onClick={onClick} title="List these technologies"
      className="text-left underline-offset-2 hover:underline">
      <span className={`tabular-nums ${strong ? 'font-bold' : 'font-medium'} text-grayscale-1300`}>{fmtN(n)}</span>
      {of > 0 && <span className="block text-[11px] text-grayscale-1100 tabular-nums">{fmtPct(pctOf(n, of))}</span>}
    </button>
  ) : <span className="text-grayscale-600">—</span>);

  const mix = (counts: CatCounts) => (
    <div className="w-32">
      <StackBar height="h-2.5" minLabelPct={101}
        segs={CATEGORIES.map(c => ({ key: c.key, n: counts[c.key], color: c.color, ink: c.ink,
          title: `${c.no}. ${c.label}: ${fmtN(counts[c.key])}` }))} />
    </div>
  );

  const td = 'px-3 py-2 align-top border-t border-grayscale-200';
  return (
    <Section title="By university"
      right={
        <div className="flex items-center gap-2 text-xs text-grayscale-1100">
          <span>Sort category columns by</span>
          <Select value={measure} onChange={v => setMeasure(v as 'share' | 'count')}
            options={['share', 'count']} labels={{ share: 'share of the university', count: 'count' }} />
        </div>
      }>
      <p className="text-xs text-grayscale-1100 -mt-1">
        One row per university, inside the filters above. Click a university&apos;s name to filter the whole
        page to it (click again to clear); click any count to list those technologies.
        <b> Disclosed by TTO</b> = categories 1+2+3; <b>Evidence-backed</b> = categories 1+4.
      </p>
      {error && <Msg kind="error">{error}</Msg>}
      {loading ? <Loading label="Counting per university…" /> : !rows.length ? (
        <div className="text-sm text-grayscale-1100 py-4 text-center">No university matches these filters.</div>
      ) : (
        <div className="overflow-auto rounded-lg border border-grayscale-300" style={{ maxHeight: '70vh' }}>
          <table className="text-sm border-separate border-spacing-0 min-w-max w-full">
            <thead className="sticky top-0 z-20">
              <tr>
                <Th label="University" k="name" sort={sort} onSort={clickSort} className="sticky left-0 z-30 border-r border-grayscale-300" />
                <Th label="Country" k="country" sort={sort} onSort={clickSort} />
                <Th label="Technologies" k="total" sort={sort} onSort={clickSort} />
                {CATEGORIES.map(c => (
                  <Th key={c.key} k={c.key} sort={sort} onSort={clickSort} title={`${c.no}. ${c.label} — click to sort by ${measure}`}
                    label={
                      <span className="inline-flex items-start gap-1 w-36 whitespace-normal leading-snug font-normal">
                        <CatDot cat={c} /><span>{c.label}</span>
                      </span>
                    } />
                ))}
                <Th label="Mix" />
                <Th label="Disclosed by TTO" k="disclosed" sort={sort} onSort={clickSort} title="Categories 1+2+3 as a share of the university's technologies" />
                <Th label="Evidence-backed" k="evidence" sort={sort} onSort={clickSort} title="Categories 1+4 as a share of the university's technologies" />
                <Th label="Weak evidence" k="weak" sort={sort} onSort={clickSort} title="Inside category 5: pass 1 found evidence too weak to assign a level" />
              </tr>
            </thead>
            <tbody>
              <tr className="bg-primary-50">
                <td className={`${td} sticky left-0 z-[5] bg-primary-50 border-r border-grayscale-300 font-bold text-grayscale-1300`}>
                  All universities{selected.length ? ' (filtered)' : ''}
                </td>
                <td className={td} />
                <td className={td}>{countCell(t.total, 0, () => onOpen({}, 'All technologies matching the filters'), true)}</td>
                {CATEGORIES.map(c => (
                  <td key={c.key} className={td}>
                    {countCell(t.counts[c.key], t.total, () => onOpen({ categories: [c.key] }, c.label), true)}
                  </td>
                ))}
                <td className={td}>{mix(t.counts)}</td>
                <td className={`${td} tabular-nums font-bold`}>{fmtPct(allDisclosed)}</td>
                <td className={`${td} tabular-nums font-bold`}>{fmtPct(allEvidence)}</td>
                <td className={td}>{countCell(t.weak, t.total, () => onOpen({ weak_evidence: true }, `All universities · ${WEAK_LABEL.toLowerCase()}`), true)}</td>
              </tr>
              {view.map(r => {
                const on = selected.length === 1 && selected[0] === r.code;
                return (
                  <tr key={r.code} className="group hover:bg-grayscale-200">
                    <td className={`${td} sticky left-0 z-[5] bg-white group-hover:bg-grayscale-200 border-r border-grayscale-300`}>
                      <button type="button" onClick={() => onToggleUniversity(r.code)}
                        title={on ? 'Filtering the page to this university — click to clear' : 'Filter the page to this university'}
                        className="text-left w-48 md:w-60 whitespace-normal break-words">
                        <span className={`block leading-tight font-bold hover:underline underline-offset-2 ${on ? 'text-primary-700' : 'text-grayscale-1300'}`}>
                          {r.name || r.code}
                        </span>
                        <span className="block text-[11px] font-mono text-grayscale-1100">{r.code}</span>
                      </button>
                    </td>
                    <td className={`${td} text-grayscale-1200 whitespace-nowrap`}>{r.country || '—'}</td>
                    <td className={td}>{countCell(r.total, 0, () => onOpen({ universities: [r.code] }, r.name || r.code), true)}</td>
                    {CATEGORIES.map(c => (
                      <td key={c.key} className={td}>
                        {countCell(r.counts[c.key], r.total,
                          () => onOpen({ universities: [r.code], categories: [c.key] }, `${r.name || r.code} · ${c.label}`))}
                      </td>
                    ))}
                    <td className={td}>{mix(r.counts)}</td>
                    <td className={`${td} tabular-nums`}>{fmtPct(r.disclosed)}</td>
                    <td className={`${td} tabular-nums`}>{fmtPct(r.evidence)}</td>
                    <td className={td}>
                      {countCell(r.weak, r.total, () => onOpen({ universities: [r.code], weak_evidence: true },
                        `${r.name || r.code} · ${WEAK_LABEL.toLowerCase()}`))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
};

// ---------------------------------------------------------------------------
// Drill-down: the technologies behind any count
// ---------------------------------------------------------------------------
// Shape: admin_trl_provenance.technologies().rows.
interface TechRow {
  utech_technology_id: string; title: string; university_code: string; university_name: string; country: string;
  category: string; weak_evidence: boolean;
  displayed_trl: number; disclosed_trl: number | null; disclosed_trl_text: string | null;
  evidence_trl: number | null; evidence_trl_text: string | null; calculated_trl_text: string | null;
  confidence_score: number | null; pass1_confidence: number | null; pass2_confidence: number | null;
  statement: string; evidence: string; industry: string; url: string;
}

const DRILL_COLS: { key: keyof TechRow; label: string; title?: string }[] = [
  { key: 'utech_technology_id', label: 'ID' },
  { key: 'title', label: 'Title' },
  { key: 'university_name', label: 'University' },
  { key: 'country', label: 'Country' },
  { key: 'category', label: 'Category' },
  { key: 'displayed_trl', label: 'Displayed TRL', title: 'What the card shows (none = no level)' },
  { key: 'disclosed_trl', label: 'Disclosed TRL', title: 'What the TTO disclosed (original_trl, first number)' },
  { key: 'evidence_trl', label: 'Evidence TRL', title: 'The level the evidence supports (pass2_trl)' },
  { key: 'confidence_score', label: 'Confidence', title: 'confidence_score, 0-5' },
  { key: 'pass1_confidence', label: 'Pass 1 conf.' },
  { key: 'pass2_confidence', label: 'Pass 2 conf.' },
  { key: 'industry', label: 'Industry', title: 'First major industry category' },
];

const show = (v: any): string => (v === null || v === undefined || v === '' ? '—' : String(v));
const PAGE_MAX = 200;
const CELL_CAP = 2000;

/** The exact rows of one disclosed × evidence cell. The backend has no
 *  disclosed-TRL filter, so: ask for the cell's evidence column sorted by
 *  disclosed TRL descending (ties by university, id — a stable order), skip
 *  the `offset` rows the grid says sit above this cell, take `n`, and keep
 *  only rows that really have this pair (so a corpus that moved between the
 *  grid and this read shows fewer rows, never wrong ones). */
const useCellRows = (filterParams: Record<string, string>, cell: Cell | undefined) => {
  const [st, setSt] = useState<{ rows: TechRow[]; loading: boolean; error: string }>(
    { rows: [], loading: !!cell, error: '' });
  const key = cell ? JSON.stringify([filterParams, cell]) : '';
  useEffect(() => {
    if (!cell) { setSt({ rows: [], loading: false, error: '' }); return; }
    let live = true;
    (async () => {
      setSt({ rows: [], loading: true, error: '' });
      const want = Math.min(cell.n, CELL_CAP);
      const first = Math.floor(cell.offset / PAGE_MAX);
      const last = Math.floor((cell.offset + want - 1) / PAGE_MAX);
      const got: TechRow[] = [];
      for (let p = first; p <= last; p++) {
        const r = await panelGet('universities', 'trl_technologies',
          { ...filterParams, sort: 'disclosed_trl', dir: 'desc', per_page: PAGE_MAX, page: p });
        if (!live) return;
        if (r.error) { setSt({ rows: [], loading: false, error: r.error }); return; }
        got.push(...(Array.isArray(r.data?.rows) ? r.data.rows : []));
      }
      const start = cell.offset - first * PAGE_MAX;
      const rows = got.slice(start, start + want)
        .filter(t => Number(t.disclosed_trl) === cell.d && Number(t.evidence_trl) === cell.e);
      setSt({ rows, loading: false, error: '' });
    })();
    return () => { live = false; };
  }, [key]);   // eslint-disable-line react-hooks/exhaustive-deps
  return st;
};

/** One server page of the drill-down; idle (no request at all) while the
 *  drill-down is narrowed to a matrix cell. Last request wins. */
const useTechPage = (params: Record<string, string | number>, enabled: boolean) => {
  const [st, setSt] = useState<{ data: any; loading: boolean; error: string }>(
    { data: null, loading: enabled, error: '' });
  const seq = useRef(0);
  const key = enabled ? JSON.stringify(params) : '';
  useEffect(() => {
    const mine = ++seq.current;
    if (!enabled) { setSt({ data: null, loading: false, error: '' }); return; }
    setSt(s => ({ ...s, loading: true, error: '' }));
    panelGet('universities', 'trl_technologies', params).then(({ data, error }) => {
      if (mine === seq.current) setSt({ data, loading: false, error });
    });
  }, [key]);   // eslint-disable-line react-hooks/exhaustive-deps
  return st;
};

const DrillDown: React.FC<{
  base: Filters; drill: Drill;
  describe: (k: ListKey, v: string) => string;
  onScope: (s: Scope) => void; onDropCell: () => void; onClose: () => void;
  onDownload: (p: Record<string, string>) => void; dlBusy: boolean;
}> = ({ base, drill, describe, onScope, onDropCell, onClose, onDownload, dlBusy }) => {
  const { scope, title, cell } = drill;
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState('50');
  const [sort, setSort] = useState<SortState>({ key: '', dir: 'asc' });
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [techId, setTechId] = useState<string | null>(null);

  const narrowed = useMemo(() => narrow(base, scope), [base, scope]);
  const filterParams = useMemo(() => (narrowed ? toParams(narrowed) : {}), [narrowed]);
  const fkey = JSON.stringify(filterParams);
  // A new slice starts on its first page with nothing expanded.
  useEffect(() => { setPage(0); setOpenRow(null); }, [fkey, perPage, sort.key, sort.dir, !!cell]);

  // Server-paged unless narrowed to one matrix cell (loaded whole, paged here).
  const reqParams: Record<string, string | number> = { ...filterParams, page, per_page: perPage };
  if (sort.key) { reqParams.sort = sort.key; reqParams.dir = sort.dir; }
  const server = useTechPage(reqParams, !cell && !!narrowed);
  const cellSt = useCellRows(filterParams, narrowed ? cell : undefined);

  const pp = Number(perPage);
  let rows: TechRow[]; let totalRows: number; let loading: boolean; let error: string; let hasData: boolean;
  if (cell) {
    const sorted = !sort.key ? cellSt.rows : [...cellSt.rows].sort((a: any, b: any) => {
      const va = a[sort.key]; const vb = b[sort.key];
      const dir = sort.dir === 'asc' ? 1 : -1;
      if (va === null || va === undefined || va === '') return 1;
      if (vb === null || vb === undefined || vb === '') return -1;
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb), undefined, { numeric: true }) * dir;
    });
    totalRows = sorted.length;
    rows = sorted.slice(page * pp, (page + 1) * pp);
    loading = cellSt.loading; error = cellSt.error; hasData = !cellSt.loading;
  } else {
    const raw: any = server.data;
    rows = Array.isArray(raw?.rows) ? raw.rows : [];
    totalRows = num(raw?.total);
    loading = server.loading; error = server.error; hasData = !!raw || !narrowed;
  }
  const pages = Math.max(1, Math.ceil(totalRows / pp));

  const clickSort = (k: string) => setSort(s => (s.key !== k ? { key: k, dir: 'asc' }
    : s.dir === 'asc' ? { key: k, dir: 'desc' } : { key: '', dir: 'asc' }));

  const scopeChips: { label: string; value: string; clear: () => void }[] = [];
  if (cell) {
    scopeChips.push({ label: 'Cell', value: `disclosed TRL ${cell.d} → evidence TRL ${cell.e}`, clear: onDropCell });
  }
  for (const k of LIST_KEYS) {
    const v = scope[k];
    if (v && v.length) {
      scopeChips.push({ label: LIST_LABELS[k], value: v.map(x => describe(k, x)).join(', '),
        clear: () => { const n = { ...scope }; delete n[k]; onScope(n); } });
    }
  }
  if (scope.direction) {
    scopeChips.push({ label: 'Direction', value: DIRECTIONS[scope.direction] || scope.direction,
      clear: () => { const n = { ...scope }; delete n.direction; onScope(n); } });
  }
  if (scope.weak_evidence) {
    scopeChips.push({ label: 'Only', value: WEAK_LABEL,
      clear: () => { const n = { ...scope }; delete n.weak_evidence; onScope(n); } });
  }

  return (
    <Section title={`Technologies — ${title}`}
      right={
        <div className="flex items-center gap-2 flex-wrap">
          <Btn small kind="primary" onClick={() => onDownload(filterParams)} disabled={dlBusy || !narrowed}
            title={cell ? `The workbook for evidence TRL ${cell.e} in these categories (every disclosed level — the export has no disclosed-TRL filter)`
              : 'The same workbook, filtered to exactly this list'}>
            {dlBusy ? 'Preparing…' : 'Download this list (Excel)'}
          </Btn>
          <Btn small onClick={onClose}>Close</Btn>
        </div>
      }>
      <div className="flex items-center gap-1.5 flex-wrap text-xs text-grayscale-1100">
        {scopeChips.map(c => <Chip key={c.label} label={c.label} value={c.value} onClear={c.clear} />)}
        <span>{scopeChips.length ? 'within' : 'All of'} the page filters above.</span>
      </div>
      {error && <Msg kind="error">{error}</Msg>}
      {cell && hasData && !error && totalRows < Math.min(cell.n, CELL_CAP) && (
        <div className="text-xs rounded-lg px-3 py-2 border text-amber-700 bg-amber-50 border-amber-200">
          Found {fmtN(totalRows)} of the {fmtN(cell.n)} technologies in this cell — the corpus changed since the grid
          was read. Refresh the page for the current numbers.
        </div>
      )}
      {cell && cell.n > CELL_CAP && (
        <div className="text-xs text-grayscale-1100">Showing the first {fmtN(CELL_CAP)} of {fmtN(cell.n)}.</div>
      )}
      <div className="flex items-center gap-2 flex-wrap text-xs text-grayscale-1100">
        <span>
          {loading && !hasData ? 'Loading…'
            : `${fmtN(totalRows)} technolog${totalRows === 1 ? 'y' : 'ies'} · page ${page + 1} of ${fmtN(pages)}`}
          {sort.key && ` · sorted by ${DRILL_COLS.find(c => c.key === sort.key)?.label || sort.key} ${sort.dir === 'asc' ? '↑' : '↓'}`}
        </span>
        <Btn small onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0 || loading}>← Prev</Btn>
        <Btn small onClick={() => setPage(p => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1 || loading}>Next →</Btn>
        <Select value={perPage} onChange={setPerPage} options={['25', '50', '100', '200']}
          labels={{ '25': '25 per page', '50': '50 per page', '100': '100 per page', '200': '200 per page' }} />
        {loading && hasData && <span>Updating…</span>}
      </div>
      {loading && !hasData ? <Loading /> : !rows.length ? (
        <div className="text-sm text-grayscale-1100 py-4 text-center">
          {narrowed ? 'No technologies in this slice.' : 'Nothing in common with the page filters above — this list is empty.'}
        </div>
      ) : (
        <div className={`overflow-auto rounded-lg border border-grayscale-300 ${loading ? 'opacity-60' : ''}`} style={{ maxHeight: '75vh' }}>
          <table className="text-sm border-separate border-spacing-0 min-w-max w-full">
            <thead className="sticky top-0 z-20">
              <tr>
                <Th label="" className="w-6" />
                {DRILL_COLS.map(c => (
                  <Th key={c.key} label={c.label} k={c.key} title={c.title} sort={sort} onSort={clickSort}
                    className="whitespace-nowrap" />
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const id = String(r.utech_technology_id ?? i);
                const rowKey = `${id}-${i}`;
                const expanded = openRow === rowKey;
                const cat = catOf(r.category);
                const td = 'px-3 py-1.5 border-t border-grayscale-200 align-top';
                const evidence = String(r.evidence ?? '');
                return (
                  <React.Fragment key={rowKey}>
                    <tr className={`hover:bg-grayscale-200 cursor-pointer ${expanded ? 'bg-primary-50' : ''}`}
                      onClick={() => setOpenRow(expanded ? null : rowKey)}>
                      <td className={`${td} text-grayscale-1100 text-xs`} title={expanded ? 'Hide statement and evidence' : 'Show statement and evidence'}>
                        {expanded ? '▾' : '▸'}
                      </td>
                      <td className={`${td} whitespace-nowrap`}>
                        <button type="button" onClick={e => { e.stopPropagation(); setTechId(id); }}
                          title="Open the full technology card"
                          className="font-mono text-xs text-primary-700 underline underline-offset-2">{id}</button>
                      </td>
                      <td className={`${td} max-w-[22rem]`}>
                        <span className="block truncate text-grayscale-1300" title={r.title}>{show(r.title)}</span>
                      </td>
                      <td className={`${td} max-w-[14rem]`}>
                        <span className="block truncate text-grayscale-1200" title={r.university_name}>{show(r.university_name)}</span>
                      </td>
                      <td className={`${td} whitespace-nowrap text-grayscale-1200`}>{show(r.country)}</td>
                      <td className={`${td} w-60`}>
                        {cat ? (
                          <span className="flex items-start gap-1.5" title={`${cat.no}. ${cat.label}`}>
                            <CatDot cat={cat} />
                            <span className="text-[11px] leading-snug text-grayscale-1200 whitespace-normal">
                              {cat.label}{r.weak_evidence ? ' (weak evidence)' : ''}
                            </span>
                          </span>
                        ) : show(r.category)}
                      </td>
                      <td className={`${td} tabular-nums text-center`} title={r.calculated_trl_text || undefined}>
                        {r.displayed_trl ? r.displayed_trl : 'none'}
                      </td>
                      <td className={`${td} tabular-nums text-center`} title={r.disclosed_trl_text ? `As written: ${r.disclosed_trl_text}` : undefined}>
                        {show(r.disclosed_trl)}
                      </td>
                      <td className={`${td} tabular-nums text-center`} title={r.evidence_trl_text ? `As written: ${r.evidence_trl_text}` : undefined}>
                        {show(r.evidence_trl)}
                      </td>
                      <td className={`${td} tabular-nums text-center`}>{show(r.confidence_score)}</td>
                      <td className={`${td} tabular-nums text-center`}>{show(r.pass1_confidence)}</td>
                      <td className={`${td} tabular-nums text-center`}>{show(r.pass2_confidence)}</td>
                      <td className={`${td} max-w-[12rem]`}>
                        <span className="block truncate text-grayscale-1200" title={r.industry}>{show(r.industry)}</span>
                      </td>
                    </tr>
                    {expanded && (
                      <tr className="bg-primary-50">
                        <td />
                        <td colSpan={DRILL_COLS.length} className="px-3 pb-3 pt-1">
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 max-w-[72rem]">
                            <div className="flex flex-col gap-1">
                              <div className="text-[11px] uppercase tracking-wide text-grayscale-1100">TRL statement</div>
                              <div className="text-sm text-grayscale-1300 whitespace-pre-wrap break-words">
                                {r.statement ? r.statement : <span className="text-grayscale-1100">No statement recorded.</span>}
                              </div>
                              <div className="text-[11px] text-grayscale-1100 mt-1">
                                Disclosed as written: <b className="text-grayscale-1200">{show(r.disclosed_trl_text)}</b>
                                {' · '}Calculated: <b className="text-grayscale-1200">{show(r.calculated_trl_text)}</b>
                                {' · '}Evidence (pass 2): <b className="text-grayscale-1200">{show(r.evidence_trl_text)}</b>
                              </div>
                            </div>
                            <div className="flex flex-col gap-1">
                              <div className="text-[11px] uppercase tracking-wide text-grayscale-1100">Evidence (pass 1)</div>
                              <div className="text-sm text-grayscale-1300 whitespace-pre-wrap break-words">
                                {evidence ? `${evidence}${evidence.length >= 300 ? '…' : ''}`
                                  : <span className="text-grayscale-1100">No evidence recorded.</span>}
                              </div>
                              {r.url && (
                                <a href={r.url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                                  className="text-[11px] text-primary-700 underline underline-offset-2 break-all mt-1">
                                  University page ↗
                                </a>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {techId && <TechCardModal id={techId} onClose={() => setTechId(null)} />}
    </Section>
  );
};

export default TrlProvenance;
